// Webhooks Events — LEFT pane (task [3.4], ws-f74mb). A scrollable, master-list
// of delivered/failed events. Selection is client-side state owned by the page
// (index.tsx); this pane is presentational + emits onSelect. Infinite-scroll
// load-more fires as a bottom sentinel enters the scroll container's viewport.
//
// Why a bespoke IntersectionObserver here and NOT src/lib/use-in-view.ts: that
// hook has `once` semantics (it latches inView=true then disconnects) and is
// disabled under prefers-reduced-motion — by contract a progressive-enhancement
// signal that "MUST NOT gate content existence." Load-more is functional, must
// re-fire on every approach to the bottom, and must work under reduced-motion —
// so this observer re-subscribes on each hasNextPage/isFetchingNextPage change.
import { useEffect, useRef } from 'react';

import type { WebhookEvent } from '@/api/webhooks/mock';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

// Compact relative-time formatter. Mirrors webhooks/index.tsx's identity-block
// formatter (which notes no shared relative-time util exists in the repo, same
// as delivery-log inlining its own absolute formatter). A local copy keeps this
// child module acyclic — exporting the page-file's copy would make index.tsx and
// this component import each other.
const relativeFmt = new Intl.RelativeTimeFormat('en-US', { numeric: 'auto' });
function formatRelative(iso: string): string {
	const then = new Date(iso).getTime();
	if (Number.isNaN(then)) return '—';
	const deltaSec = Math.round((then - Date.now()) / 1000);
	const abs = Math.abs(deltaSec);
	const steps: [Intl.RelativeTimeFormatUnit, number][] = [
		['year', 60 * 60 * 24 * 365],
		['month', 60 * 60 * 24 * 30],
		['day', 60 * 60 * 24],
		['hour', 60 * 60],
		['minute', 60],
	];
	for (const [unit, secs] of steps) {
		if (abs >= secs) return relativeFmt.format(Math.round(deltaSec / secs), unit);
	}
	return relativeFmt.format(deltaSec, 'second');
}

export interface EventListProps {
	events: WebhookEvent[];
	selectedId: string | null;
	onSelect: (id: string) => void;
	hasNextPage: boolean;
	isFetchingNextPage: boolean;
	onLoadMore: () => void;
}

export function EventList({
	events,
	selectedId,
	onSelect,
	hasNextPage,
	isFetchingNextPage,
	onLoadMore,
}: EventListProps) {
	const scrollRef = useRef<HTMLDivElement>(null);
	const sentinelRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const sentinel = sentinelRef.current;
		if (!sentinel || typeof IntersectionObserver === 'undefined') return;
		if (!hasNextPage) return;

		const observer = new IntersectionObserver(
			(entries) => {
				if (
					entries.some((e) => e.isIntersecting) &&
					hasNextPage &&
					!isFetchingNextPage
				) {
					onLoadMore();
				}
			},
			// Root is the scroll container so it triggers on internal scroll; the
			// margin arms the fetch slightly before the sentinel is fully visible.
			{ root: scrollRef.current, rootMargin: '120px' },
		);
		observer.observe(sentinel);
		return () => observer.disconnect();
	}, [hasNextPage, isFetchingNextPage, onLoadMore]);

	return (
		<div
			ref={scrollRef}
			className="max-h-[34rem] overflow-y-auto rounded-lg border border-border"
		>
			<ul className="divide-y divide-border">
				{events.map((event) => {
					const selected = event.id === selectedId;
					return (
						<li key={event.id}>
							<button
								type="button"
								onClick={() => onSelect(event.id)}
								aria-current={selected ? 'true' : undefined}
								data-event-row
								data-selected={selected ? 'true' : undefined}
								className={cn(
									'flex w-full items-center justify-between gap-3 px-4 py-3 text-left transition-colors',
									selected ? 'bg-accent' : 'hover:bg-accent/40',
								)}
							>
								<div className="min-w-0">
									<p className="truncate font-mono text-xs text-foreground">
										{event.type}
									</p>
									<p
										className="mt-0.5 text-xs text-muted-foreground"
										suppressHydrationWarning
									>
										{formatRelative(event.timestamp)}
									</p>
								</div>
								<Badge status={event.status}>
									{event.status === 'success' ? 'Success' : 'Failed'}
								</Badge>
							</button>
						</li>
					);
				})}
			</ul>

			{/* Bottom sentinel: the observer above watches this element; the text is
			    the load-more affordance / end-of-feed marker. */}
			<div
				ref={sentinelRef}
				className="px-4 py-3 text-center text-xs text-muted-foreground"
			>
				{isFetchingNextPage
					? 'Loading more…'
					: hasNextPage
						? 'Scroll for more'
						: 'End of events'}
			</div>
		</div>
	);
}
